import { Helmet } from "react-helmet-async";

/**
 * SEO - injects <title>, meta description, Open Graph, Twitter Card and JSON-LD.
 * Owns the entire dynamic <head> block; public/index.html no longer ships
 * static title/description/canonical/og/twitter so this component is the
 * single source of truth for per-page metadata.
 */
export const SEO = ({
  title,
  description,
  url,
  image,
  imageWidth = 1200,
  imageHeight = 630,
  locale = "en_US",
  type = "website",
  publishedAt,
  modifiedAt,
  keywords = [],
  jsonLd,
  noindex = false,
}) => {
  const safeTitle = title || "Resonate";
  const fullTitle = safeTitle.includes("Resonate") ? safeTitle : `${safeTitle} · Resonate`;
  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords.length > 0 && <meta name="keywords" content={keywords.join(", ")} />}
      <link rel="canonical" href={url} />
      <meta
        name="robots"
        content={noindex ? "noindex, follow" : "index, follow, max-image-preview:large, max-snippet:-1"}
      />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:site_name" content="Resonate" />
      <meta property="og:locale" content={locale} />
      {image && <meta property="og:image" content={image} />}
      {image && <meta property="og:image:width" content={String(imageWidth)} />}
      {image && <meta property="og:image:height" content={String(imageHeight)} />}
      {publishedAt && <meta property="article:published_time" content={publishedAt} />}
      {modifiedAt && <meta property="article:modified_time" content={modifiedAt} />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      {image && <meta name="twitter:image" content={image} />}

      {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
    </Helmet>
  );
};
